'use client';

import { ARIA_LABELS } from '@/constants';
import { cn } from '@/utils/cn';
import { useNavigationRouting } from '@/hooks';
import { INavItem } from '@/types';
import { NavButton } from './subcomponents/NavButton';
import { NavLogo } from './subcomponents/NavLogo';
import { ToggleButton } from '@/components/Buttons';
import React, { useState } from 'react';

/**
 * Props for Navigation component
 */
export interface NavigationProps {
  /** Children elements to be rendered inside the navigation component */
  children?: React.ReactNode;
  /** Additional class names for the navigation component */
  className?: string;
  /** Whether to show navigation controls */
  showControls?: boolean;
}

/**
 * Navigation component.
 *
 * Provides sidebar navigation with and expandable/collapsible functionality.
 */
export const Navigation: React.FC<NavigationProps> = ({ children, className, showControls }) => {
  const [backgroundClass, setBackgroundClass] = useState('bg-neutral-gray-000');
  const [internalExpanded, setInternalExpanded] = useState(false);
  const { navItems } = useNavigationRouting();
  const dynamicViewportStyle = { minHeight: '100dvh' };

  /**
   * Handle toggle of navigation expansion/collapse
   */
  const handleToggle = () => {
    const newExpanded = !internalExpanded;
    setInternalExpanded(newExpanded);
  };

  /**
   * Handle navigation item click
   */
  const handleNavItemClick = (item: INavItem) => {
    if (item.onClick) {
      item.onClick();
    }
  };

  /**
   * Classes for the sidebar navigation, which is shown on tablet and desktop viewports.
   * It is hidden on mobile viewports in favor of a bottom navigation bar.
   */
  const sidebarClasses = cn(
    'bg-brand-primary h-full p-4',
    'hidden lg:flex items-center flex-col justify-between',
    'transition-all duration-300',
    {
      'w-20': !internalExpanded,
      'w-[215px]': internalExpanded,
    },
  );

  /**
   * Classes for the bottom navigation bar, which is shown on mobile viewports.
   * It is hidden on tablet and desktop viewports in favor of a sidebar navigation.
   */
  const bottombarClasses = cn(
    'bg-brand-primary w-full py-4 px-6 pb-[calc(1rem+env(safe-area-inset-bottom))]',
    'flex lg:hidden items-center flex-row justify-between',
    'transition-all duration-300',
    'fixed bottom-0 left-0 right-0 z-50',
    'min-h-20',
  );

  /**
   * Classes for the main content area, which adjusts its layout based on whether the
   * navigation controls are shown or not. When controls are shown, it has no left border
   * to seamlessly integrate with the sidebar. When controls are hidden, it has a full
   * border to create separation from the edge of the screen. The background color and
   * border styles also adapt based on the state of the navigation and the viewport size.
   */
  const mainContentClasses = cn(
    'flex-1',
    'overflow-hidden',
    'bg-neutral-gray-000',
    'border-0 lg:border-[1rem] lg:border-brand-primary',
    'lg:ring-[1rem] lg:ring-inset lg:ring-brand-primary',
    { '!border-s-0': showControls },
  );

  // ============================================================
  // Main Render
  // ============================================================
  return (
    <div
      className={cn('flex h-dvh transition-all duration-300 ease-in-out', className)}
      style={dynamicViewportStyle}
    >
      {/* Sidebar Navigation */}
      {showControls && (
        <>
          {/* [Tablet/Desktop] Sidebar Navigation */}
          <aside aria-label="Sidebar" className={sidebarClasses}>
            {/* Top section with logo and navigation items */}
            <div className="flex w-full flex-col items-start gap-6">
              <NavLogo isLoading={navItems && navItems.length === 0} />
              <nav
                aria-label={ARIA_LABELS.MAIN_NAVIGATION}
                className="flex w-full flex-col gap-2"
                role="navigation"
              >
                {navItems.map((item) => (
                  <NavButton
                    key={item.id}
                    item={item}
                    expanded={internalExpanded}
                    onClick={() => handleNavItemClick(item)}
                  />
                ))}
              </nav>
            </div>

            {/* Bottom section with toggle button */}
            {navItems && navItems.length > 0 && (
              <div className="flex w-full justify-end">
                <ToggleButton isExpanded={internalExpanded} onClick={handleToggle} />
              </div>
            )}
          </aside>

          {/* [Mobile] Sidebar Navigation */}
          <aside aria-label="Bottom navigation" className={bottombarClasses}>
            <nav
              aria-label={ARIA_LABELS.MAIN_NAVIGATION}
              className={cn(`grid w-full gap-8 grid-cols-${navItems.length}`)}
              role="navigation"
            >
              {navItems.map((item) => (
                <div className="col-span-1" key={item.id}>
                  <NavButton
                    item={item}
                    expanded={internalExpanded}
                    onClick={() => handleNavItemClick(item)}
                  />
                </div>
              ))}
            </nav>
          </aside>
        </>
      )}

      {/* Main Content Area */}
      <main aria-label="Main content" className={mainContentClasses}>
        <div className="h-full w-full overflow-hidden lg:rounded-2xl">
          <div className={cn('flex h-screen', backgroundClass)} style={dynamicViewportStyle}>
            <div
              className={cn(
                'flex-1',
                'mb-[calc(5rem+env(safe-area-inset-bottom))] lg:mb-8',
                'overflow-x-hidden overflow-y-hidden xs:overflow-y-scroll',
              )}
              style={{ scrollbarWidth: 'auto' }}
            >
              <div className="p-4 lg:p-6">{children}</div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

Navigation.displayName = 'Navigation';

export default Navigation;
